import * as React from 'react';
import { useEffect, useState, useRef, useMemo } from 'react';
import { contingencyService } from '../../coordinar/services/contingencyService';
import { Chip } from '@vadiun/react-components';
import { ChoferPartialModel } from 'app/chofer/models/Chofer';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { NewTable, NewTableColumn } from 'shared/components/NewTable';
import { TransferReservationManagmentModel } from '../../gestion/models/TransferReservationManagment';
import { copyReservationPublicNewLinkToClipboard } from '../../viajeTraslado/services/copyReservationPublicLinkToClipboard';
import { TransferReservationOpenedModel } from '../models/TransferReservationOpened';
import Client from '../../coordinar/components/list/client';
import { TransferReservationPendingModel } from '../../pendiente/models/TransferReservationPending';
import ServiceType from '../../coordinar/components/list/serviceType';
import PassengersItem from 'app/viaje/components/shared/passengers';
import { OpenedTravelColumnVisibility } from '../utils/openedStorage';
import { ColorState } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';
import Points from '../../coordinar/components/list/points';
import { useResponsiveBreakpoint } from '@vadiun/react-hooks';
import { useNotificationsContext } from 'shared/context/notificationContext';
import WaitingClockTimer from './WaitingClockTimer';
import ExpandableTextCell from 'shared/components/Table/ExpandableTextCell';
import ActionsItemOpened from './list/ActionsItemOpened';

interface TableQueryData {
  data: TransferReservationOpenedModel[];
  total: number;
  isLoading: boolean;
  error: Error | undefined;
  serverSideOptions: any;
}

interface Props {
  tableQuery: TableQueryData;
  handlePageChange: (currentPage: number) => void;
  onLocationsDetails: (reservation: TransferReservationOpenedModel) => void;
  onReservationCopy: (reservation: TransferReservationManagmentModel) => void;
  onForceEnd: (reservation: TransferReservationOpenedModel) => void;
  setOpenToPending: (id: number) => void;
  onReAssignDriver: (
    reservation: TransferReservationOpenedModel,
    isContingency: boolean
  ) => void;
  onShowDriverDetails: (chofer: ChoferPartialModel, busVehicle?: any) => void;
  onEditReservation: (travel: TransferReservationOpenedModel) => void;
  onCancelReservation: (reservation: TransferReservationOpenedModel) => void;
  onShowTravelDetails: (id: number) => React.ReactNode;
  onDelayNotify: (travel: TransferReservationOpenedModel) => void;
  onBusesPreAssign: (
    travel: TransferReservationPendingModel | TransferReservationOpenedModel
  ) => void;
  columnVisibility?: OpenedTravelColumnVisibility;
  onChangePage: (newPage: number) => void;
  onChangeRowsPerPage: (newRowsPerPage: number) => void;
  onSortChange?: (direction: string | null, sortBy: string) => void;
}

type OpenedRow = TransferReservationOpenedModel & {
  schedule: { arrival: any; exit: any; end: any };
  passengers: string[];
  color: string;
};

export function ViajeTrasladoOpenedTable({
  tableQuery,
  handlePageChange,
  onReservationCopy,
  onLocationsDetails,
  onReAssignDriver,
  onShowDriverDetails,
  onEditReservation,
  setOpenToPending,
  onCancelReservation,
  onShowTravelDetails,
  onForceEnd,
  onDelayNotify,
  onBusesPreAssign,
  columnVisibility,
  onChangePage,
  onChangeRowsPerPage
}: Props) {
  const { totalStatesReservations } = useNotificationsContext();
  const { reservationStateChanged } = totalStatesReservations;
  const snackbar = useSnackbar();
  const { getActiveContingencies } = contingencyService();
  const display = useResponsiveBreakpoint();
  const isMobileOrTablet =
    display === 'xs' || display === 'sm' || display === 'md';

  const [reservationButtonElement, setReservationButtonElement] = useState<
    (EventTarget & HTMLElement) | null
  >(null);
  const eventIndex = useRef<number | null>(null);

  const [reAssignButtonElement, setReAssignButtonElement] = useState<
    (EventTarget & HTMLElement) | null
  >(null);
  const reAssignEventIndex = useRef<number | null>(null);

  const [isLoadingReAssign, setIsLoadingReAssign] = useState(false);
  const [hasActiveContingencyReAssign, setHasActiveContingencyReAssign] =
    useState(false);

  const [data, setData] = useState<OpenedRow[]>([]);

  useEffect(() => {
    setData(
      (tableQuery.data ?? []).map(
        (reservation: TransferReservationOpenedModel) => ({
          ...reservation,
          schedule: {
            arrival: reservation.reservationDatetime,
            exit: reservation.departureDatetime,
            end: reservation.arrivalDateTime
          },
          passengers: reservation.locations
            .map((location) =>
              location.passengers.map((passenger) => passenger.name)
            )
            .flat(),
          color:
            reservation.is_contingency &&
            reservation.semaphore?.color === ColorState.VIOLET
              ? ColorState.VIOLET
              : reservation.semaphore?.color ?? ''
        })
      )
    );
  }, [tableQuery.data]);

  useEffect(() => {
    if (!reservationStateChanged?.id || reservationStateChanged.id === 0)
      return;

    setData((prev: OpenedRow[]) => {
      const updated = prev.map((item: any) =>
        item.id === reservationStateChanged.id
          ? {
            ...item,
            color: reservationStateChanged.color ?? item.color,
            state: reservationStateChanged.state ?? item.state,
            order:
              reservationStateChanged.color === ColorState.RED
                ? reservationStateChanged.order ?? item.order
                : item.order,
            show_waiting_clock:
              reservationStateChanged.show_waiting_clock ??
              item.show_waiting_clock,
            state_datetime: reservationStateChanged.state_datetime
              ? moment(reservationStateChanged.state_datetime)
              : item.state_datetime,
            total_minutes_waiting:
              reservationStateChanged.total_minutes_waiting ??
              item.total_minutes_waiting
          }
          : item
      );

      const colorPriority = (color: string) =>
        color === ColorState.RED
          ? 0
          : color === ColorState.ORANGE
            ? 1
            : color === ColorState.YELLOW
              ? 2
              : 3;

      return [...updated].sort((a, b) => {
        const pa = colorPriority(a.color);
        const pb = colorPriority(b.color);
        if (pa !== pb) return pa - pb;
        if (pa === 0) return (b.order ?? 0) - (a.order ?? 0);
        return 0;
      });
    });
  }, [reservationStateChanged]);

  const columns = useMemo<NewTableColumn<OpenedRow>[]>(
    () => [
      {
        key: 'schedule_service',
        label: 'Horario y servicio',
        width: '120px',
        align: 'center',
        render: (row, index) => {
          const isDifferentDay = !row.schedule.exit.isSame(moment(), 'day');
          return (
            <div className="flex flex-col gap-4 justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-ink dark:text-ink-dark">
                  {row.schedule.arrival.format('HH:mm')}
                </span>
                <span className="text-xs text-inkMuted dark:text-inkMuted-dark">
                  (
                  {Math.abs(
                    row.schedule.end.diff(row.schedule.arrival, 'minutes')
                  )}{' '}
                  min.)
                </span>
              </div>
              {/* Servicio e indicador de vuelo si aplica */}
              <div className="flex flex-col gap-2 items-center">
                <ServiceType
                  data={data}
                  index={index}
                  isMobileOrTablet={false}
                />
                {row.event_name && (
                  <span
                    className="flex items-center gap-1 text-xs text-gray-500 dark:text-inkMuted-dark"
                    title={row.event_name}
                  >
                    ✈️{' '}
                    <span className="truncate max-w-[120px]">
                      {row.event_name}
                    </span>
                  </span>
                )}
              </div>
            </div>
          );
        }
      },
      {
        key: 'client_passengers',
        label: 'Cliente y Pasajeros',
        width: '180px',
        align: 'center',
        render: (row, index) => (
          <div className="flex flex-col gap-4 justify-center">
            <div className="text-sm font-medium">
              <Client current={row} isMobileOrTablet={false} />
            </div>
            <div className="text-xs">
              <PassengersItem passengers={row.passengers} idTravel={row.id} />
            </div>
          </div>
        )
      },
      {
        key: 'directions',
        label: 'Domicilios',
        width: {
          '2xl': '360px'
        },
        align: 'center',
        render: (row, index) => (
          <Points
            data={data}
            index={index}
            isMobileOrTablet={isMobileOrTablet}
            onLocationsDetails={onLocationsDetails}
          />
        )
      },
      {
        key: 'group_operational',
        label: 'Chofer y Estado',
        width: '260px',
        align: 'center',
        render: (row) => {
          const waitStart = row.show_waiting_clock
            ? moment.max(row.reservationDatetime, row.state_datetime)
            : null;

          return (
            <div className="flex flex-col items-center justify-center gap-4 w-full">
              {/* Chofer Asignado */}
              <div className="flex rounded-md bg-gray-50 p-2 dark:bg-gray-900/40 w-fit">
                <div className="flex flex-col items-center justify-between">
                  <div>
                    <p
                      className="cursor-pointer text-xs font-semibold text-blue-600 hover:underline dark:text-blue-400"
                      onClick={() =>
                        onShowDriverDetails(
                          row.assigned_driver!,
                          row.serviceType === 'Buses'
                            ? row.bus_requirement?.vehicle
                            : undefined
                        )
                      }
                      role="button"
                      tabIndex={0}
                    >
                      {row.assigned_driver?.codigo || 'Sin chofer'} -{' '}
                      {row.assigned_driver?.nombreCompleto}
                    </p>
                  </div>
                  {row.serviceType === 'Buses' &&
                    row.bus_requirement?.vehicle?.patent && (
                      <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
                        {row.bus_requirement.vehicle.patent}
                      </span>
                    )}
                </div>
              </div>
              {/* Estado y Tiempos de espera */}
              <Chip
                variant="light"
                className="whitespace-nowrap"
                color={row.color === ColorState.VIOLET ? 'purple' : row.color}
              >
                {row.state === 'Inició espera' ? 'En espera' : row.state}
              </Chip>
              {waitStart && <WaitingClockTimer startAt={waitStart} />}
              {row.total_minutes_waiting > 0 && (
                <span className="text-[11px] text-gray-700 dark:text-inkMuted-dark">
                  Espera: {row.total_minutes_waiting} min.
                </span>
              )}
            </div>
          );
        }
      },
      {
        key: 'group_observations',
        label: 'Observaciones',
        width: { '2xl': '180px', '3xl': '200px' },
        align: 'center',
        render: (row) => (
          <div className="flex flex-col gap-4 text-xs w-36">
            {row.coordinationObservation && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-inkMuted dark:text-inkMuted-dark">
                  Coord:{' '}
                </span>
                <ExpandableTextCell
                  text={row.coordinationObservation}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.general_observation && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-inkMuted dark:text-inkMuted-dark">
                  Chofer:{' '}
                </span>
                <ExpandableTextCell
                  text={row.general_observation}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
          </div>
        )
      },
      {
        key: 'group_additional',
        label: 'Información Adicional',
        width: { '2xl': '160px' },
        align: 'center',
        render: (row) => (
          <div className="flex flex-col gap-4 text-xs w-36">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink dark:text-ink-dark text-sm">
                {row.id}
              </span>
            </div>
            {row.platformId && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-inkMuted dark:text-inkMuted-dark">
                  Plataforma:
                </span>
                <ExpandableTextCell
                  text={row.platformId}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.event_name && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-inkMuted dark:text-inkMuted-dark">
                  Evento:
                </span>
                <ExpandableTextCell
                  text={row.event_name}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
            {row.setback_comments && (
              <div className="flex flex-col items-start text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-inkMuted dark:text-inkMuted-dark block mb-0.5">
                  Contratiempos:
                </span>
                <ExpandableTextCell
                  text={row.setback_comments}
                  maxWidthClass="w-full"
                  isMobileOrTablet={isMobileOrTablet}
                />
              </div>
            )}
          </div>
        )
      },
      {
        key: 'actions',
        label: 'Acciones',
        align: 'center',
        width: '100px',
        render: (row) => (
          <ActionsItemOpened
            row={row}
            onReAssignDriver={onReAssignDriver}
            getActiveContingencies={getActiveContingencies}
            onShowTravelDetails={onShowTravelDetails}
            onEditReservation={onEditReservation}
            onCancelReservation={onCancelReservation}
            onReservationCopy={onReservationCopy}
            onForceEnd={onForceEnd}
            copyReservationPublicNewLinkToClipboard={
              copyReservationPublicNewLinkToClipboard
            }
            setOpenToPending={setOpenToPending}
            onDelayNotify={onDelayNotify}
            onBusesPreAssign={onBusesPreAssign}
          />
        )
      }
    ],
    [
      data,
      isMobileOrTablet,
      isLoadingReAssign,
      reAssignButtonElement,
      reservationButtonElement,
      onShowDriverDetails,
      onLocationsDetails,
      getActiveContingencies,
      onReAssignDriver,
      onShowTravelDetails,
      onEditReservation,
      onCancelReservation,
      onReservationCopy,
      onForceEnd,
      setOpenToPending,
      onDelayNotify,
      onBusesPreAssign,
      snackbar
    ]
  );

  const currentPage = (tableQuery.serverSideOptions?.page ?? 0) + 1;
  const currentRowsPerPage = tableQuery.serverSideOptions?.rowsPerPage ?? 100;

  const getColorStyle = (colorValue: string) => {
    switch (colorValue) {
      case ColorState.RED:
        return 'border-l-4 border-r-4 border-l-redTrafficLight border-r-redTrafficLight dark:border-l-redTrafficLight dark:border-r-redTrafficLight';
      case ColorState.ORANGE:
        return 'border-l-4 border-r-4 border-l-orangeTrafficLight border-r-orangeTrafficLight dark:border-l-orangeTrafficLight dark:border-r-orangeTrafficLight';
      case ColorState.YELLOW:
        return 'border-l-4 border-r-4 border-l-yellowTrafficLight border-r-yellowTrafficLight dark:border-l-yellowTrafficLight dark:border-r-yellowTrafficLight';
      case ColorState.VIOLET:
        return 'border-l-4 border-r-4 border-l-purple-600 border-r-purple-600 dark:border-l-purple-400 dark:border-r-purple-400';
      default:
        return '';
    }
  };

  const parsedColumnVisibility = useMemo(() => {
    if (!columnVisibility) return undefined;
    return Object.keys(columnVisibility).reduce((acc, key) => {
      acc[key] = Boolean((columnVisibility as any)[key]);
      return acc;
    }, {} as Record<string, boolean>);
  }, [columnVisibility]);

  return (
    <NewTable<OpenedRow>
      columns={columns}
      columnVisibility={parsedColumnVisibility}
      data={data}
      getRowKey={(row) => row.id}
      isLoading={tableQuery.isLoading}
      getRowClassName={(row) => getColorStyle(row.color)}
      getRowAnimation={(row, index) => {
        const latestId = reservationStateChanged?.id ?? 0;
        const shouldPulse =
          latestId === row.id && row.color !== ColorState.GREEN;
        const colorValue = row.color || '';

        const animationName = shouldPulse ? `shadowPulse-${colorValue}` : '';

        return {
          initial: { opacity: 0, y: 8 },
          animate: { opacity: 1, y: 0 },
          transition: { delay: index * 0.05, duration: 0.2 },
          style:
            shouldPulse && animationName
              ? {
                  animation: `${animationName} 1.5s ease-in-out infinite`
                }
              : undefined
        };
      }}
      page={currentPage}
      total={tableQuery.total}
      rowsPerPage={currentRowsPerPage}
      onChangePage={(newPage) => {
        onChangePage(newPage);
        handlePageChange(newPage);
      }}
      onChangeRowsPerPage={onChangeRowsPerPage}
      rowsPerPageOptions={[10, 30, 50, 100, 200, 300, 400, 500]}
      stickyHeader={{ top: '113px', zIndex: 1 }}
      emptyTitle="No hay viajes abiertos"
    />
  );
}
